// ============================================================================
// PARTICIPANTS DRAWER
// Facilitator panel: show QR codes per attendee, join status, regenerate.
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { X, RefreshCw, QrCode, CheckCircle, Clock, Loader } from 'lucide-react';
import { api } from '../../services/api';

interface Participant {
  participantId: string;
  name?: string;
  role?: string;
  organization?: string;
  personType?: 'attendee' | 'facilitator';
  status: 'joined' | 'pending';
  joinedAt?: string;
  expiry: string;
  qrDataUrl: string;
  qrUrl: string;
}

interface Props {
  sessionId: string;
  onClose: () => void;
}

export const ParticipantsDrawer: React.FC<Props> = ({ sessionId, onClose }) => {
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [loading, setLoading] = useState(true);
  const [qrModal, setQrModal] = useState<Participant | null>(null);
  const [regenerating, setRegenerating] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    try {
      const res = await api.get<{ participants: Participant[] }>(`/api/sessions/${sessionId}/participants`);
      setParticipants(res.data.participants);
    } catch { /* silent */ } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => { load(); }, [load]);

  async function regenerate(participantId: string) {
    setRegenerating(prev => new Set(prev).add(participantId));
    try {
      const res = await api.post<Participant>(`/api/sessions/${sessionId}/participants/${participantId}/regenerate`);
      setParticipants(prev => prev.map(p =>
        p.participantId === participantId ? { ...p, ...res.data, status: 'pending' } : p
      ));
    } catch { /* silent */ } finally {
      setRegenerating(prev => { const s = new Set(prev); s.delete(participantId); return s; });
    }
  }

  const joined = participants.filter(p => p.status === 'joined').length;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />
      <div className="fixed right-0 top-0 bottom-0 w-80 bg-[#0f2027] border-l border-white/10 z-50 flex flex-col shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div>
            <h2 className="text-white font-semibold text-base">Participants</h2>
            <p className="text-white/40 text-xs mt-0.5">{joined}/{participants.length} joined</p>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto py-3">
          {loading && (
            <div className="flex items-center justify-center py-10">
              <Loader size={20} className="text-white/30 animate-spin" />
            </div>
          )}
          {!loading && participants.length === 0 && (
            <div className="text-center py-10 px-5">
              <p className="text-white/30 text-sm">No attendees on this session yet — add them in Session Setup</p>
            </div>
          )}
          {!loading && participants.length > 0 && (
            participants.map(p => {
              let statusText: React.ReactNode;
              if (p.personType === 'facilitator') {
                statusText = <span className="text-primary/70">Facilitator{p.role ? ` · ${p.role}` : ''}</span>;
              } else if (p.role && p.organization) {
                statusText = `${p.role} · ${p.organization}`;
              } else {
                statusText = p.role || p.organization || (p.status === 'joined' ? 'Joined' : 'Not yet joined');
              }
              return (
              <div key={p.participantId} className="flex items-center gap-3 px-5 py-3 hover:bg-white/5 transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    {p.status === 'joined' ? (
                      <CheckCircle size={14} className="text-primary flex-shrink-0" />
                    ) : (
                      <Clock size={14} className="text-white/30 flex-shrink-0" />
                    )}
                    <p className="text-white text-sm font-medium truncate">
                      {p.name || p.participantId}
                    </p>
                  </div>
                  <p className="text-white/30 text-xs mt-0.5 ml-5">
                    {statusText}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setQrModal(p)}
                    className="p-1.5 text-white/40 hover:text-primary transition-colors"
                    title="Show QR code"
                  >
                    <QrCode size={16} />
                  </button>
                  <button
                    onClick={() => regenerate(p.participantId)}
                    disabled={regenerating.has(p.participantId)}
                    className="p-1.5 text-white/40 hover:text-yellow-400 transition-colors"
                    title="Regenerate QR code"
                  >
                    {regenerating.has(p.participantId) ? (
                      <Loader size={16} className="animate-spin" />
                    ) : (
                      <RefreshCw size={16} />
                    )}
                  </button>
                </div>
              </div>
              );
            })
          )}
        </div>
      </div>

      {qrModal && (
        <div className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-6" onClick={() => setQrModal(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-xs w-full text-center" onClick={e => e.stopPropagation()}>
            <p className="font-bold text-slate-900 mb-1">{qrModal.name || qrModal.participantId}</p>
            <p className="text-slate-400 text-xs mb-4">Scan to join the participant view</p>
            <img src={qrModal.qrDataUrl} alt="QR code" className="w-48 h-48 mx-auto mb-4" />
            <p className="text-slate-400 text-xs break-all">{qrModal.qrUrl}</p>
            <button onClick={() => setQrModal(null)} className="mt-4 text-slate-500 text-sm">Close</button>
          </div>
        </div>
      )}
    </>
  );
};
